import { urls } from '@game/constants';
import type { ClientSoundPath } from '@game/shared-types';
import { getRandomNumberInRange } from '@game/utils';
import { Howl } from 'howler';

import { Logger } from '@util/logger';
import { persistentStore } from '@store/persistentStore';

import { playGeneratedSfx, sfxDefinitions } from './sfxSynth';

export type SfxKey = keyof typeof sfxDefinitions;

function playHowl(src: string[]): void {
  const volume = persistentStore.getState().sfxVolume / 100;
  if (volume === 0) {
    return;
  }
  const howl = new Howl({
    src,
    volume,
    onend: (): void => {
      howl.unload();
    },
    onloaderror: (_id, error): void => {
      Logger.error('SFX load error', src, error);
      howl.unload();
    },
    onplayerror: (_id, error): void => {
      Logger.error('SFX play error', src, error);
      howl.unload();
    },
  });
  howl.play();
}

function isSoundPath(path: unknown): path is ClientSoundPath {
  return typeof path === 'string' && path.endsWith('.m4a');
}

export function playSfx(key: SfxKey | ClientSoundPath): void {
  const volume = persistentStore.getState().sfxVolume / 100;
  if (volume === 0) {
    return;
  }

  if (isSoundPath(key)) {
    playHowl(key.split(', ').map((filename) => `${urls.cdnAudio}sfx/${filename}`));
    return;
  }

  playGeneratedSfx(sfxDefinitions[key](), volume);
}

/**
 * Plays a skill's usage sound, falling back to ui_blip_low if the skill has no usageSfx defined.
 * Does nothing if used by an NPC or if combat SFX is disabled.
 *
 * @param filenames - filenames from the skill's usageSfx (without CDN prefix), or undefined
 * @param isNpc - whether the skill is being used by an NPC (NPCs don't play skill sounds)
 */
export function playSkillSfx(sounds: ClientSoundPath | undefined, isNpc: boolean): void {
  if (!persistentStore.getState().sfxCombatEnabled || isNpc) {
    return;
  }

  if (sounds) {
    playHowl(sounds.split(', ').map((filename) => `${urls.cdnAudio}sfx/${filename}`));
  } else {
    playSfx('ui_blip_low');
  }
}

/**
 * Plays an NPC-specific hit sound. Does nothing if combat SFX is disabled.
 *
 * @param hitSfx - filenames from the NPC's hitSfx (without CDN prefix)
 */
export function playHitSfx(hitSfx: ClientSoundPath[], isNpc: boolean): void {
  if (!persistentStore.getState().sfxCombatEnabled || !isNpc) {
    return;
  }

  const hitIndex = getRandomNumberInRange(0, hitSfx.length - 1);
  // Choose a random hit SFX from the array (some NPCs have multiple for variety)
  const randomHitSfx = hitSfx[hitIndex];

  if (!randomHitSfx) {
    Logger.warn('No hit SFX found for NPC', hitSfx);
    return;
  }

  playHowl(randomHitSfx.split(', ').map((filename) => `${urls.cdnAudio}sfx/${filename}`));
}

/**
 * Plays an NPC-specific killing blow sound, falling back to a random generated one if
 * the NPC has no killingBlowSfx defined.
 *
 * @param npcFilenames - filenames from the NPC's killingBlowSfx (without CDN prefix), or undefined
 */
export function playKillingBlowSfx(npcFilenames?: ClientSoundPath[]): void {
  if (!persistentStore.getState().sfxCombatEnabled) {
    return;
  }

  if (npcFilenames && npcFilenames.length > 0) {
    // Choose a random killing blow SFX from the array (some NPCs have multiple for variety)
    const randomKillingBlowSfx = npcFilenames[Math.floor(Math.random() * npcFilenames.length)];

    if (!randomKillingBlowSfx) {
      Logger.warn('No killing blow SFX found for NPC', npcFilenames);
      return;
    }

    playHowl(randomKillingBlowSfx.split(', ').map((filename) => `${urls.cdnAudio}sfx/${filename}`));
  } else {
    // Get all sfx keys that start with 'combat_killing_blow'
    const killingBlowKeys = Object.keys(sfxDefinitions).filter((key) => key.startsWith('combat_killing_blow')) as SfxKey[];

    // Pick a random one (fallback to base sound if none found)
    const randomKey =
      killingBlowKeys.length > 0 ? killingBlowKeys[Math.floor(Math.random() * killingBlowKeys.length)]! : ('combat_killing_blow' as SfxKey);
    playSfx(randomKey);
  }
}
